import type {TransactionalMessageBanner as TransactionalMessageBannerType} from '@github-ui/code-view-types'
import type {NormalizedSequenceString} from '@github-ui/hotkey'
import {TransactionalMessageBanner} from '@github-ui/in-product-targeting'
import {NavigableRefProvider} from '@github-ui/navigable-ref'
import {getKeybindings} from '@github-ui/ui-commands'
import {GitPullRequestIcon, IssueOpenedIcon, RepoIcon} from '@primer/octicons-react'
import {Stack} from '@primer/react'
import {clsx} from 'clsx'
import {useMemo} from 'react'

import type {GlobalNavMenuItem, WithShow} from '../menu/GlobalNavMenu'
import {ItemDivider} from '../primitives/ItemDivider'
import {NavLinkItem} from '../primitives/NavItemLink'
import {TopNav, TopNavProvider} from '../primitives/TopNav'
import type {ContextRegionPayload} from '../types'
import type {GlobalNavUserMenuProps} from '../user-menu/GlobalNavUserMenu'
import {GlobalNavAnalyticsProvider} from '../utils/Analytics'
import {DeferredNavPayloadProvider} from '../utils/DeferredNavPayloadContext'
import {HideOnError} from '../utils/HideOnError'
import styles from './GlobalNavBar.module.css'
import {ContextRegion, useRegisterContextRegion} from './items/ContextRegion'
import {CopilotItems, type CopilotItemsProps} from './items/CopilotItems'
import {EnterpriseBanner, type EnterpriseBannerProps} from './items/EnterpriseBanner'
import {HeaderLogo, type HeaderLogoProps} from './items/HeaderLogo'
import {NotificationItem, type NotificationItemProps} from './items/Notification'
import {Search, type SearchProps} from './items/Search'
import {LazyAccountSwitchDialog} from './LazyAccountSwitchDialog'
import {LazyGlobalCreateMenu} from './LazyGlobalCreateMenu'
import {LazyAnchoredGlobalNavMenu} from './LazyGlobalNavMenu'
import {LazyAnchoredGlobalNavUserMenu} from './LazyGlobalNavUserMenu'
import {LocalNavigation} from './LocalNavigation'

type LinkItemProps = Partial<WithShow<Pick<GlobalNavMenuItem, 'href' | 'commandId'>>>

export interface GlobalNavBarProps {
  /** Props to control the right, user menu */
  userMenu: Pick<GlobalNavUserMenuProps, 'owner'>

  /** Information about the user's notifications */
  notifications?: NotificationItemProps

  /** Props for the main header logo */
  headerLogo?: HeaderLogoProps

  /** Config options for the issues menu bar link */
  issues?: LinkItemProps

  /** Config options for the pull requests menu bar link */
  pulls?: LinkItemProps

  /** Config options for the contributed repositories menu bar link */
  contributedRepos?: LinkItemProps

  /** Context region data with breadcrumb navigation */
  contextRegion?: ContextRegionPayload

  /** Config options for the copilot menu and buttons */
  copilot?: CopilotItemsProps

  /** Props for the enterprise banner shown above the nav bar */
  enterpriseBanner?: EnterpriseBannerProps

  /** Props for the search buttons */
  search?: SearchProps

  /** Global transactional message banner to display below the nav bar */
  globalTransactionalMessage?: TransactionalMessageBannerType | null

  /** URL for fetching payload data */
  payloadsUrl?: string
}

export function GlobalNavBar(props: GlobalNavBarProps) {
  const {
    userMenu,
    headerLogo,
    copilot,
    notifications,
    contextRegion,
    search,
    enterpriseBanner,
    issues,
    pulls,
    contributedRepos,
    globalTransactionalMessage,
    payloadsUrl,
  } = props
  const hasLocalNavigation = !!contextRegion?.localNavigation && contextRegion.localNavigation.length > 0
  const contextRegionProps = useRegisterContextRegion()

  const localNavigationKeybindings = useMemo(
    () =>
      new Set(
        contextRegion?.localNavigation?.flatMap(item => (item.commandId ? getKeybindings(item.commandId) : [])) ?? [],
      ),
    [contextRegion?.localNavigation],
  )

  return (
    <NavigableRefProvider>
      <GlobalNavAnalyticsProvider category="GLOBAL_NAV_BAR">
        <TopNavProvider
          hasLocalNavigation={hasLocalNavigation}
          isSearchPage={search?.isSearchPage ?? false}
          isJumpToSearch={search?.isJumpToSearch ?? false}
          payloadsUrl={payloadsUrl}
        >
          <DeferredNavPayloadProvider>
            <TopNav>
              <TopNav.Above>
                <EnterpriseBanner {...enterpriseBanner} />
              </TopNav.Above>
              <TopNav.Left>
                <HideOnError>
                  <LazyAnchoredGlobalNavMenu />
                </HideOnError>
                <HeaderLogo {...headerLogo} />
              </TopNav.Left>

              <TopNav.Center className={clsx(!contextRegion?.crumbs?.length && styles.noBreadcrumbs)}>
                <HideOnError>
                  <ContextRegion {...contextRegionProps} crumbs={contextRegion?.crumbs} />
                </HideOnError>
                <HideOnError>
                  <Search {...search} />
                </HideOnError>
              </TopNav.Center>

              <TopNav.Right>
                <Stack
                  data-testid="top-bar-actions"
                  direction="horizontal"
                  align="center"
                  gap="condensed"
                  padding="none"
                >
                  <HideOnError>
                    <CopilotItems {...copilot} />
                  </HideOnError>

                  <ItemDivider />

                  <HideOnError>
                    <LazyGlobalCreateMenu />
                  </HideOnError>

                  <GlobalNavBarLinkItems
                    issues={issues}
                    pulls={pulls}
                    contributedRepos={contributedRepos}
                    localNavKeybindings={localNavigationKeybindings}
                  />
                </Stack>
                <NotificationItem {...notifications} />
                <HideOnError>
                  <LazyAnchoredGlobalNavUserMenu {...userMenu} />
                </HideOnError>
                <LazyAccountSwitchDialog />
              </TopNav.Right>

              {hasLocalNavigation && (
                <HideOnError>
                  <LocalNavigation {...contextRegion} />
                </HideOnError>
              )}
            </TopNav>
            {globalTransactionalMessage && (
              <HideOnError>
                <TransactionalMessageBanner banner={globalTransactionalMessage} />
              </HideOnError>
            )}
          </DeferredNavPayloadProvider>
        </TopNavProvider>
      </GlobalNavAnalyticsProvider>
    </NavigableRefProvider>
  )
}

function GlobalNavBarLinkItems({
  localNavKeybindings,
  ...props
}: Pick<GlobalNavBarProps, keyof typeof GlobalNavBarItems> & {localNavKeybindings: Set<NormalizedSequenceString>}) {
  return (
    <Stack direction="horizontal" align="center" gap="condensed" padding="none" className="hide-sm hide-md">
      {(['issues', 'pulls', 'contributedRepos'] as const).map(key => {
        const {label, ...navItemProps} = GlobalNavBarItems[key]

        const {show, ...mergedProps} = {...navItemProps, ...props[key]}

        if (show === false) {
          return null
        }

        // Suppress this global nav command if a local nav item has the same keybinding. This is a hack because we are
        // bound by legacy support for the same keybinding across the global and local "go to issues/PRs". UI commands
        // doesn't (yet) support a concept of 'priority' for global commands, so we just don't bind the command at all.
        const keybindings = getKeybindings(mergedProps.commandId)
        const hasConflictWithLocalNav = keybindings.some(kb => localNavKeybindings.has(kb))
        const withCommandIdOverride = {...mergedProps, ...(hasConflictWithLocalNav ? {commandId: undefined} : {})}

        return <NavLinkItem key={key} {...withCommandIdOverride} aria-label={label} />
      })}
    </Stack>
  )
}

/**
 * The items in the global navigation bar
 * Any config set here will be the default, and can be overridden by passing props to the GlobalNavBar component
 */
export const GlobalNavBarItems = {
  issues: {
    href: '/issues',
    icon: IssueOpenedIcon,
    label: 'All issues',
    analyticsKey: 'ISSUES_HEADER',
    show: true,
    commandId: 'global-navigation:go-to-issues',
  },
  pulls: {
    href: '/pulls',
    icon: GitPullRequestIcon,
    label: 'All pull requests',
    analyticsKey: 'PULL_REQUESTS_HEADER',
    show: true,
    commandId: 'global-navigation:go-to-pull-requests',
  },
  contributedRepos: {
    href: '/repos',
    label: 'All repositories',
    analyticsKey: 'REPOS_HEADER',
    icon: RepoIcon,
    show: false,
    commandId: 'global-navigation:go-to-repositories',
  },
} as const satisfies Record<string, WithShow<Omit<GlobalNavMenuItem, 'key'>>>
